"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { ParetoChart } from "@/components/tools/color-charts/pareto-svg";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type NumberFormat,
  percentText,
  valueText,
} from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type LabeledTable,
  parseLabeledRows,
} from "@/lib/tool-kit/charts/parse";
import {
  buildPareto,
  type OtherMode,
  type ParetoResult,
} from "@/lib/tool-kit/color-charts/pareto";

/** English strings. A translated content file overrides these through `ui`. */
export const PARETO_CHART_MAKER_STRINGS = {
  dataLabel: "Your data — one category per line: label, count",
  dataHint:
    "Separate cells with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. A first line without numbers is a header. Categories are sorted largest first; a label that appears twice is added together.",
  exampleData:
    "Error type, Count\nMissing DOI or URL, 46\nInconsistent author names, 31\nList not in alphabetical order, 19\nMissing page numbers, 14\nTitle capitalization, 11\nIncorrect italics, 8\nMissing publisher, 5\nWrong date format, 4",
  firstLineHeader: "The first line is a header",
  exampleTitle: "Citation Errors Found in 40 Student Bibliographies",
  exampleCategoryTitle: "Type of error",
  exampleCountTitle: "Number of errors",
  cumulativeTitle: "Cumulative percent",
  thresholdText: "80%",
  otherLabel: "Other",
  otherModeLabel: "Combine small categories into “Other”",
  otherNone: "Do not combine",
  otherTop: "Keep the largest N",
  otherBelow: "Combine those under N%",
  otherTopField: "Number of categories to keep",
  otherBelowField: "Combine categories under this percent of the total",
  otherHint:
    "“Other” is always drawn last, however large it is. Two or more categories must qualify, since combining a single one would only rename it.",
  shadeLabel: "Shade the categories that reach 80%",
  total: "Total",
  categories: "Categories",
  reach: "To reach 80%",
  reachValue: "{count} of {all} ({percent})",
  results: "Result",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "Category axis title",
  yAxisTitle: "Count axis title",
  axisTitlePlaceholderX: "Category",
  axisTitlePlaceholderY: "Count",
  colorLabel: "Bar color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the counts and cumulative percentages",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  categoryColumn: "Category",
  countColumn: "Count",
  shareColumn: "Share (%)",
  cumulativeColumn: "Cumulative (%)",
  chartLabel: "Pareto chart",
  chartDescription:
    "Pareto chart of {categories}. The largest, {topLabel}, is {topShare} of the total of {total}. The first {count} reach {reached} of the total, passing the 80% line.",
  categoriesDetected: "one:{count} category|other:{count} categories",
  enterData:
    "Enter at least one line with a label and a number to draw the chart.",
  zeroTotal: "Every count is zero, so there is nothing to divide into shares.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  extraColumns:
    "Only the first number column is used. A Pareto chart has one count per category.",
  negativeValues:
    "one:{count} negative or invalid count was left out — a count cannot be part of a whole.|other:{count} negative or invalid counts were left out — a count cannot be part of a whole.",
  mergedLabels:
    "one:{count} repeated label was added into the first entry with that label.|other:{count} repeated labels were added into the first entry with that label.",
  groupedNote:
    "one:{count} category is combined into “Other”.|other:{count} categories are combined into “Other”.",
  tooMany: "Only the first {max} categories are read.",
} as const;

type Strings = typeof PARETO_CHART_MAKER_STRINGS;

const MAX_CATEGORIES = 60;

function summarize(
  pareto: ParetoResult,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string {
  const top = pareto.rows[0];
  const reached = pareto.rows[Math.max(0, pareto.vitalFew - 1)];
  return t(strings.chartDescription, {
    categories: tPlural(
      strings.categoriesDetected,
      locale,
      pareto.rows.length,
      {
        count: fmt(pareto.rows.length),
      }
    ),
    topLabel: top?.label ?? "",
    topShare: top ? percentText(fmt, top.share) : "",
    total: valueText(fmt, pareto.total),
    count: fmt(pareto.vitalFew),
    reached: reached ? percentText(fmt, reached.cumulative) : "",
  });
}

function paretoNotices(
  table: LabeledTable,
  pareto: ParetoResult,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const notes: string[] = [];
  if (table.rows.length === 0) {
    notes.push(strings.enterData);
  } else if (pareto.total === 0) {
    notes.push(strings.zeroTotal);
  }
  if (table.skipped.length > 0) {
    notes.push(
      tPlural(strings.skippedLines, locale, table.skipped.length, {
        lines: table.skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (table.seriesCount > 1) {
    notes.push(strings.extraColumns);
  }
  const bad = pareto.negative + table.invalidCells;
  if (bad > 0) {
    notes.push(
      tPlural(strings.negativeValues, locale, bad, { count: fmt(bad) })
    );
  }
  if (pareto.merged > 0) {
    notes.push(
      tPlural(strings.mergedLabels, locale, pareto.merged, {
        count: fmt(pareto.merged),
      })
    );
  }
  if (pareto.grouped > 0) {
    notes.push(
      tPlural(strings.groupedNote, locale, pareto.grouped, {
        count: fmt(pareto.grouped),
      })
    );
  }
  if (table.rows.length > MAX_CATEGORIES) {
    notes.push(t(strings.tooMany, { max: fmt(MAX_CATEGORIES) }));
  }
  return notes;
}

export default function ParetoChartMakerWidget() {
  const strings = useToolUi(PARETO_CHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [otherMode, setOtherMode] = useState<OtherMode>("none");
  const [otherText, setOtherText] = useState("");
  const [shade, setShade] = useState(true);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleCategoryTitle,
    yTitle: strings.exampleCountTitle,
    color: "blue",
    valueLabels: true,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseLabeledRows(raw, { header: headerChoice ?? undefined });
  const entries = table.rows.slice(0, MAX_CATEGORIES).map((row) => ({
    label: row.label,
    value: row.values[0] ?? Number.NaN,
  }));
  const pareto = buildPareto(entries, {
    otherMode,
    otherLimit: parseNumberInput(otherText),
    otherLabel: strings.otherLabel,
  });
  const drawable = pareto.rows.length > 0 && pareto.total > 0;
  const reached = pareto.rows[Math.max(0, pareto.vitalFew - 1)];
  const notices = paretoNotices(table, pareto, strings, locale, fmt);
  const head = [
    strings.categoryColumn,
    strings.countColumn,
    strings.shareColumn,
    strings.cumulativeColumn,
  ];
  const percentValue = (share: number) => Math.round(share * 10_000) / 100;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="pcm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      <Checkbox
        checked={headerChoice ?? table.autoHeader}
        label={strings.firstLineHeader}
        onChange={setHeaderChoice}
      />
      <div aria-live="polite" className="space-y-2">
        {notices.map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="space-y-3">
        <PillGroup
          label={strings.otherModeLabel}
          name="pcm-other"
          onChange={setOtherMode}
          options={[
            { value: "none", label: strings.otherNone },
            { value: "top", label: strings.otherTop },
            { value: "below", label: strings.otherBelow },
          ]}
          value={otherMode}
        />
        {otherMode === "none" ? null : (
          <TextField
            id="pcm-other-limit"
            label={
              otherMode === "top"
                ? strings.otherTopField
                : strings.otherBelowField
            }
            numeric
            onChange={setOtherText}
            value={otherText}
          />
        )}
        <p className={HINT}>{strings.otherHint}</p>
        <Checkbox
          checked={shade}
          label={strings.shadeLabel}
          onChange={setShade}
        />
      </div>
      {drawable ? (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <StatGrid>
            <StatCard
              label={strings.total}
              value={valueText(fmt, pareto.total)}
            />
            <StatCard
              label={strings.categories}
              value={fmt(pareto.rows.length)}
            />
            <StatCard
              emphasis
              label={strings.reach}
              value={t(strings.reachValue, {
                count: fmt(pareto.vitalFew),
                all: fmt(pareto.rows.length),
                percent: reached ? percentText(fmt, reached.cumulative) : "",
              })}
            />
          </StatGrid>
        </div>
      ) : null}
      {drawable ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([
            head,
            ...pareto.rows.map((row) => [
              row.label,
              row.value,
              percentValue(row.share),
              percentValue(row.cumulative),
            ]),
          ])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "pareto-chart")}
          labels={strings}
          note={style.note}
          table={{
            head,
            rows: pareto.rows.map((row) => [
              row.label,
              valueText(fmt, row.value),
              percentText(fmt, row.share),
              percentText(fmt, row.cumulative),
            ]),
          }}
          title={style.title}
        >
          <ParetoChart
            cumulativeTitle={strings.cumulativeTitle}
            description={summarize(pareto, strings, locale, fmt)}
            fmt={fmt}
            id="pcm-chart"
            label={style.title.trim() || strings.chartLabel}
            rows={pareto.rows}
            shade={shade}
            showValues={style.valueLabels}
            style={style}
            thresholdText={strings.thresholdText}
            total={pareto.total}
            vitalFew={pareto.vitalFew}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        axisLabels={{
          xAxisTitle: strings.xAxisTitle,
          yAxisTitle: strings.yAxisTitle,
          axisTitlePlaceholderX: strings.axisTitlePlaceholderX,
          axisTitlePlaceholderY: strings.axisTitlePlaceholderY,
        }}
        colorLabels={strings}
        idPrefix="pcm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
